import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/analytics";

// endpoint: "bookings" | "enrollments". Controlled mode: pass open + onOpenChange (no trigger).
export default function BookingDialog({ trigger, title = "Prenota una consulenza", endpoint = "bookings", presets = {}, open: openProp, onOpenChange, source = "" }) {
  const [openState, setOpenState] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_date: "", message: "", ...presets });
  const open = openProp ?? openState;
  const setOpen = (v) => (onOpenChange ? onOpenChange(v) : setOpenState(v));
  const serviceLabel = presets.service || presets.course || title;

  useEffect(() => {
    if (open) track("book_click", undefined, { service: serviceLabel, source });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = endpoint === "enrollments"
        ? { ...form, course: presets.course || presets.service || title, level: presets.level || "", mode: form.mode || presets.mode || "online" }
        : { ...form, service: presets.service || "", type: presets.type || "consulenza" };
      const { data } = await api.post(`/${endpoint}`, payload);
      track("book_submit", undefined, { service: serviceLabel, source });
      toast.success(data.message);
      setOpen(false);
      setForm({ name: "", email: "", phone: "", preferred_date: "", message: "", ...presets });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const field = "w-full bg-transparent border-b border-[#2A1F14]/15 py-2.5 text-sm text-[#2A1F14] focus:border-gold outline-none transition-colors placeholder:text-[#2A1F14]/30";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="bg-[#EFE3CE] border border-[#2A1F14]/10 text-[#2A1F14] max-w-md z-[200]">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#2A1F14]">{title}</DialogTitle>
          <DialogDescription className="text-[#2A1F14]/55 text-sm">Compila i campi: ti ricontatteremo per confermare data e modalità.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-2" data-testid="booking-form">
          <input required placeholder="Nome e cognome" value={form.name} onChange={set("name")} className={field} data-testid="booking-name" />
          <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field} data-testid="booking-email" />
          <input placeholder="Telefono (opzionale)" value={form.phone} onChange={set("phone")} className={field} data-testid="booking-phone" />
          {endpoint === "enrollments" && (
            <select value={form.mode || presets.mode || "online"} onChange={set("mode")} className={field + " bg-[#EFE3CE]"} data-testid="booking-mode">
              <option value="online">Online</option>
              <option value="residenziale">In presenza (residenziale)</option>
            </select>
          )}
          {endpoint === "bookings" && (
            <input placeholder="Data preferita (opzionale)" value={form.preferred_date} onChange={set("preferred_date")} className={field} data-testid="booking-date" />
          )}
          <textarea placeholder="Il tuo messaggio (opzionale)" value={form.message} onChange={set("message")} rows={3} className={field + " resize-none"} data-testid="booking-message" />
          <button disabled={loading} type="submit" data-testid="booking-submit" className="w-full py-3 rounded-full bg-gold text-black text-sm font-medium tracking-wide hover:bg-[#D4A056] transition-colors disabled:opacity-50">
            {loading ? "Invio..." : "Invia richiesta"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
